/**
 * KAYA — Product Card Image Carousel
 *
 * Manual-only carousel (NO autoplay) for product cards in collection grids.
 * Supports: arrows, touch swipe, trackpad / mouse-wheel, keyboard, pointer drag.
 * Reinitialises safely on Shopify section reload / infinite-scroll re-render.
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     CONSTANTS
     ----------------------------------------------------------------------- */
  var SELECTOR_CAROUSEL = '[data-pc-carousel]';
  var SELECTOR_TRACK    = '[data-pc-track]';
  var ATTR_INIT         = 'data-pc-initialized';

  // Wheel / trackpad tuning
  var WHEEL_COOLDOWN_MS = 300;   // min ms between slide changes via wheel
  var DRAG_THRESHOLD    = 10;    // px before we commit to a drag
  var SWIPE_THRESHOLD   = 30;    // px minimum to count as a swipe

  /* -----------------------------------------------------------------------
     CAROUSEL CLASS
     ----------------------------------------------------------------------- */
  function ProductCardCarousel(el) {
    this.el        = el;
    this.track     = el.querySelector(SELECTOR_TRACK);
    this.slides    = Array.prototype.slice.call(this.track.children);
    this.count     = this.slides.length;
    this.index     = 0;

    this.btnPrev   = el.querySelector('.pc-carousel__btn--prev');
    this.btnNext   = el.querySelector('.pc-carousel__btn--next');

    // State
    this._wheelLastTime = 0;
    this._engaged       = false;  // user has started interacting
    this._dragging      = false;
    this._dragStartX    = 0;
    this._dragDelta     = 0;
    this._touchStartX   = 0;
    this._touchStartY   = 0;
    this._touchLocked   = false; // direction lock for touch

    // Bound handlers (for removal)
    this._onPrev       = this._goPrev.bind(this);
    this._onNext       = this._goNext.bind(this);
    this._onWheel      = this._handleWheel.bind(this);
    this._onKeydown    = this._handleKeydown.bind(this);

    this._onTouchStart = this._handleTouchStart.bind(this);
    this._onTouchMove  = this._handleTouchMove.bind(this);
    this._onTouchEnd   = this._handleTouchEnd.bind(this);

    this._onPointerDown = this._handlePointerDown.bind(this);
    this._onPointerMove = this._handlePointerMove.bind(this);
    this._onPointerUp   = this._handlePointerUp.bind(this);

    this._init();
  }

  ProductCardCarousel.prototype._init = function () {
    if (this.count < 2) return;

    this.el.setAttribute(ATTR_INIT, 'true');

    // Arrows
    this.btnPrev.addEventListener('click', this._onPrev);
    this.btnNext.addEventListener('click', this._onNext);

    // Wheel / trackpad
    this.el.addEventListener('wheel', this._onWheel, { passive: false });

    // Touch (mobile swipe)
    this.el.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.el.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this.el.addEventListener('touchend', this._onTouchEnd, { passive: true });

    // Pointer drag (desktop click-drag)
    this.el.addEventListener('pointerdown', this._onPointerDown);

    // Keyboard (when focused)
    this.el.addEventListener('keydown', this._onKeydown);

    // Build dots
    this._buildDots();

    // Set initial button state
    this._updateControls();
  };

  /* ---------- Navigation ------------------------------------------------- */

  ProductCardCarousel.prototype._goTo = function (idx) {
    if (idx < 0 || idx >= this.count || idx === this.index) return;
    this.index = idx;
    this.track.style.transform = 'translateX(-' + (this.index * 100) + '%)';
    this._updateControls();
  };

  ProductCardCarousel.prototype._goPrev = function (e) {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    this._engaged = true;
    this._goTo(this.index - 1);
  };

  ProductCardCarousel.prototype._goNext = function (e) {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    this._engaged = true;
    this._goTo(this.index + 1);
  };

  ProductCardCarousel.prototype._updateControls = function () {
    // Disable at boundaries
    this.btnPrev.disabled = (this.index === 0);
    this.btnNext.disabled = (this.index === this.count - 1);

    // Update dots
    if (this._dots) {
      for (var i = 0; i < this._dots.length; i++) {
        if (i === this.index) {
          this._dots[i].classList.add('pc-carousel__dot--active');
        } else {
          this._dots[i].classList.remove('pc-carousel__dot--active');
        }
      }
    }
  };

  /* ---------- Dots -------------------------------------------------------- */

  ProductCardCarousel.prototype._buildDots = function () {
    if (this.count <= 1) return;

    var dotsContainer = document.createElement('div');
    dotsContainer.className = 'pc-carousel__dots';
    dotsContainer.setAttribute('aria-hidden', 'true');

    this._dots = [];
    for (var i = 0; i < this.count; i++) {
      var dot = document.createElement('span');
      dot.className = 'pc-carousel__dot' + (i === 0 ? ' pc-carousel__dot--active' : '');
      dotsContainer.appendChild(dot);
      this._dots.push(dot);
    }

    this.el.appendChild(dotsContainer);
  };

  /* ---------- Wheel / Trackpad ------------------------------------------- */

  ProductCardCarousel.prototype._handleWheel = function (e) {
    var absX = Math.abs(e.deltaX);
    var absY = Math.abs(e.deltaY);

    // Determine intent: horizontal swipe vs vertical scroll
    var intentHorizontal = false;

    if (absX > absY && absX > 2) {
      // Clear horizontal gesture (trackpad swipe)
      intentHorizontal = true;
    } else if (e.shiftKey) {
      // Shift + wheel = horizontal intent
      intentHorizontal = true;
    } else if (this._engaged && absY > 5 && absX < 2) {
      // User already engaged with carousel AND vertical-only scroll
      // Convert vertical to horizontal only if clearly interacting
      intentHorizontal = true;
    }

    if (!intentHorizontal) return; // let the page scroll normally

    // Cooldown: one slide per gesture
    var now = Date.now();
    if (now - this._wheelLastTime < WHEEL_COOLDOWN_MS) {
      e.preventDefault();
      return;
    }

    // Determine direction
    var delta = intentHorizontal && absX > absY ? e.deltaX : e.deltaY;
    if (delta === 0) return;

    // Check boundaries: don't block scroll if at edges
    if (delta > 0 && this.index >= this.count - 1) return;
    if (delta < 0 && this.index <= 0) return;

    e.preventDefault();
    this._wheelLastTime = now;
    this._engaged = true;

    if (delta > 0) {
      this._goTo(this.index + 1);
    } else {
      this._goTo(this.index - 1);
    }
  };

  /* ---------- Touch (mobile swipe) --------------------------------------- */

  ProductCardCarousel.prototype._handleTouchStart = function (e) {
    if (e.touches.length !== 1) return;
    this._touchStartX = e.touches[0].clientX;
    this._touchStartY = e.touches[0].clientY;
    this._touchLocked  = false;
  };

  ProductCardCarousel.prototype._handleTouchMove = function (e) {
    if (e.touches.length !== 1) return;

    var dx = e.touches[0].clientX - this._touchStartX;
    var dy = e.touches[0].clientY - this._touchStartY;

    if (!this._touchLocked) {
      // Determine direction lock
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 5) {
        this._touchLocked = 'h';
      } else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 5) {
        this._touchLocked = 'v';
      }
    }

    if (this._touchLocked === 'h') {
      // Only prevent default if we can actually move in this direction
      if ((dx > 0 && this.index > 0) || (dx < 0 && this.index < this.count - 1)) {
        e.preventDefault();
      }
    }
    // If vertical lock or undecided, let page scroll
  };

  ProductCardCarousel.prototype._handleTouchEnd = function (e) {
    if (this._touchLocked !== 'h') return;

    var touch = e.changedTouches[0];
    var dx = touch.clientX - this._touchStartX;

    this._engaged = true;
    if (dx > SWIPE_THRESHOLD && this.index > 0) {
      this._goTo(this.index - 1);
    } else if (dx < -SWIPE_THRESHOLD && this.index < this.count - 1) {
      this._goTo(this.index + 1);
    }
  };

  /* ---------- Pointer drag (desktop click + drag) ------------------------ */

  ProductCardCarousel.prototype._handlePointerDown = function (e) {
    // Only left-click; ignore buttons
    if (e.button !== 0) return;
    if (e.target.closest('button')) return;

    this._dragStartX = e.clientX;
    this._dragDelta  = 0;
    this._dragging   = false;

    // Bind move / up on document so we don't lose them
    document.addEventListener('pointermove', this._onPointerMove);
    document.addEventListener('pointerup', this._onPointerUp);
  };

  ProductCardCarousel.prototype._handlePointerMove = function (e) {
    var dx = e.clientX - this._dragStartX;

    if (!this._dragging && Math.abs(dx) > DRAG_THRESHOLD) {
      this._dragging = true;
      this.track.classList.add('pc-carousel__track--dragging');
      // Prevent links from firing
      this.el.style.pointerEvents = 'none';
      this.el.style.pointerEvents = '';
    }

    if (this._dragging) {
      this._dragDelta = dx;
      var baseOffset = -(this.index * 100);
      var viewportWidth = this.el.offsetWidth || 1;
      var pxToPercent = (dx / viewportWidth) * 100;
      this.track.style.transform = 'translateX(' + (baseOffset + pxToPercent) + '%)';
    }
  };

  ProductCardCarousel.prototype._handlePointerUp = function () {
    document.removeEventListener('pointermove', this._onPointerMove);
    document.removeEventListener('pointerup', this._onPointerUp);

    if (this._dragging) {
      this.track.classList.remove('pc-carousel__track--dragging');
      this._engaged = true;

      if (this._dragDelta > SWIPE_THRESHOLD && this.index > 0) {
        this._goTo(this.index - 1);
      } else if (this._dragDelta < -SWIPE_THRESHOLD && this.index < this.count - 1) {
        this._goTo(this.index + 1);
      } else {
        // Snap back
        this.track.style.transform = 'translateX(-' + (this.index * 100) + '%)';
      }

      // Briefly block clicks so the link doesn't fire after drag
      var self = this;
      var preventClick = function (evt) {
        evt.preventDefault();
        evt.stopPropagation();
        self.el.removeEventListener('click', preventClick, true);
      };
      this.el.addEventListener('click', preventClick, true);
      // Safety: remove after a tick
      setTimeout(function () {
        self.el.removeEventListener('click', preventClick, true);
      }, 100);
    }

    this._dragging  = false;
    this._dragDelta = 0;
  };

  /* ---------- Keyboard --------------------------------------------------- */

  ProductCardCarousel.prototype._handleKeydown = function (e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._goNext();
    }
  };

  /* ---------- Cleanup ---------------------------------------------------- */

  ProductCardCarousel.prototype.destroy = function () {
    this.btnPrev.removeEventListener('click', this._onPrev);
    this.btnNext.removeEventListener('click', this._onNext);
    this.el.removeEventListener('wheel', this._onWheel);
    this.el.removeEventListener('touchstart', this._onTouchStart);
    this.el.removeEventListener('touchmove', this._onTouchMove);
    this.el.removeEventListener('touchend', this._onTouchEnd);
    this.el.removeEventListener('pointerdown', this._onPointerDown);
    this.el.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('pointermove', this._onPointerMove);
    document.removeEventListener('pointerup', this._onPointerUp);
    this.el.removeAttribute(ATTR_INIT);

    // Remove dots
    var dots = this.el.querySelector('.pc-carousel__dots');
    if (dots) dots.remove();
  };

  /* -----------------------------------------------------------------------
     INIT / REINIT HELPERS
     ----------------------------------------------------------------------- */

  function initCarousels(root) {
    var containers = (root || document).querySelectorAll(SELECTOR_CAROUSEL);
    for (var i = 0; i < containers.length; i++) {
      var el = containers[i];
      if (el.getAttribute(ATTR_INIT) === 'true') continue;
      var slideCount = el.querySelector(SELECTOR_TRACK).children.length;
      if (slideCount < 2) continue;
      el._pcCarousel = new ProductCardCarousel(el);
    }
  }

  /* -----------------------------------------------------------------------
     BOOT
     ----------------------------------------------------------------------- */

  // 1. DOMContentLoaded or immediate
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCarousels(); });
  } else {
    initCarousels();
  }

  // 2. Shopify section load / re-render (theme editor, AJAX pagination, filters)
  document.addEventListener('shopify:section:load', function (e) {
    initCarousels(e.target);
  });

  // 3. MutationObserver for infinite-scroll / AJAX filter re-renders
  //    Only observe direct children additions in product grids.
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType === 1) {
          // Check if this node IS a carousel or CONTAINS carousels
          if (node.matches && node.matches(SELECTOR_CAROUSEL)) {
            if (node.getAttribute(ATTR_INIT) !== 'true') {
              node._pcCarousel = new ProductCardCarousel(node);
            }
          } else if (node.querySelectorAll) {
            initCarousels(node);
          }
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

})();
